/**
 * Render-time guard for admin/editor-entered link values (#260).
 *
 * React only *warns* on `javascript:` hrefs — it still renders them — and the
 * tenant origin also serves /admin with the admin JWT in localStorage. The
 * backend now rejects unsafe schemes on write (backend/src/utils/safe-url.ts),
 * but rows stored before that check still exist, so every sink that turns a
 * DB value into an href runs it through `safeHref` first. An unsafe value
 * yields `undefined`, and callers render the content without a link.
 */

export interface SafeHrefOptions {
  /** Allow same-site paths like `/schedule` (never `//host` or `/\host`). */
  relative?: boolean
  /** Allow `mailto:` / `tel:`. Defaults to true. */
  contact?: boolean
}

// Browsers drop tab/CR/LF anywhere in a URL and strip leading C0/space, so
// `java\nscript:` and `\u0001javascript:` still execute. Refuse any control
// character instead of trying to predict the browser's normalisation.
// eslint-disable-next-line no-control-regex
const CONTROL_RE = /[\u0000-\u001f\u007f]/

export function safeHref(value: string | null | undefined, opts: SafeHrefOptions = {}): string | undefined {
  if (typeof value !== 'string') return undefined
  const v = value.trim()
  if (!v || CONTROL_RE.test(v)) return undefined

  if (v.startsWith('/')) {
    return opts.relative && v[1] !== '/' && v[1] !== '\\' ? v : undefined
  }

  let parsed: URL
  try {
    parsed = new URL(v)
  } catch {
    return undefined
  }
  const contact = opts.contact !== false
  switch (parsed.protocol) {
    case 'http:':
    case 'https:':
      return parsed.hostname ? v : undefined
    case 'mailto:':
    case 'tel:':
      return contact ? v : undefined
    default:
      return undefined
  }
}

/** True for an absolute http(s)/mailto/tel URL — i.e. render a plain <a>, not a router <Link>. */
export function isAbsoluteHref(href: string): boolean {
  return !href.startsWith('/')
}
